// Background music on the built-in maps: Bhop By LasBoi (the game's own songs, served with the site)
// and tracks streamed from Audius (an open music platform whose public API lets apps stream its
// catalogue): the month's trending tracks of a genre, shuffled and played one after another. Audius
// tracks aren't hosted or stored by the game; each is credited on screen with a link back to it on
// Audius. Its own volume, and a mute key (M by default).

import { LASBOI_FILES } from './lasboiTracks';

export type Station = 'mix' | 'lasboi' | 'dubstep' | 'dnb' | 'electronic';

export const STATIONS: { id: Station; label: string }[] = [
  { id: 'mix', label: 'Mix (Bhop By LasBoi, Dubstep, Drum & Bass, Electronic)' },
  { id: 'lasboi', label: 'Bhop By LasBoi' },
  { id: 'dubstep', label: 'Dubstep' },
  { id: 'dnb', label: 'Drum & Bass' },
  { id: 'electronic', label: 'Electronic' },
];

/** Audius genre names. */
const GENRES: Record<Exclude<Station, 'mix' | 'lasboi'>, string> = {
  dubstep: 'Dubstep',
  dnb: 'Drum & Bass',
  electronic: 'Electronic',
};

const API = 'https://api.audius.co/v1';
const APP = 'bhop-web';

export interface Track {
  id: string;
  title: string;
  artist: string;
  /** The track's page on Audius (none for our own songs). */
  link: string | null;
  genre: string;
  /** Where it streams from. */
  src: string;
}

/** Name of the game's own songs (a station, and their genre on screen). */
export const LASBOI = 'Bhop By LasBoi';

/**
 * Tracks are mastered at very different loudness (our own songs at about -14 dB average, some Audius
 * tracks far louder or quieter), so the music is levelled while it plays: its loudness is measured
 * (roughly as ears weigh it: little bass, a bit more presence) and the gain eased toward TARGET_DB.
 */
const TARGET_DB = -20;
/** The leveller never boosts a quiet track more than this (a quiet intro isn't turned up to full). */
const MAX_BOOST = 2.5;
const MIN_GAIN = 0.1;
/** Quieter than this (a pause, a fade) isn't measured. */
const SILENCE = 1e-5;
/** How often loudness is measured (ms). */
const LEVEL_MS = 100;
/** Gain before WebAudio is running (no click yet) or if it isn't available. */
const FALLBACK_GAIN = 0.5;

/**
 * Ears hear loudness in decibels, so a linear slider is far too loud at its low end (10% is only
 * -20 dB). Squared: 10% is -40 dB (quiet background), 50% is -12 dB, 100% is full.
 */
const sliderGain = (v: number) => v * v;

/** The game's own songs, titled like their files (underscores as spaces). */
const LASBOI_TRACKS: Track[] = LASBOI_FILES.map((file) => ({
  id: 'lasboi:' + file,
  title: file.replace(/\.mp3$/, '').replace(/_/g, ' '),
  artist: 'LasBoi',
  link: null,
  genre: LASBOI,
  src: `${import.meta.env.BASE_URL ?? '/'}music/lasboi/${file}`,
}));

/** The fields of an Audius track the game reads. */
interface AudiusTrack {
  id: string;
  title: string;
  genre: string;
  duration: number;
  permalink: string;
  is_streamable?: boolean;
  is_stream_gated?: boolean;
  is_unlisted?: boolean;
  is_delete?: boolean;
  allowed_api_keys?: string[] | null;
  parental_warning_type?: string | null;
  user: { name: string; handle: string; is_deactivated?: boolean };
}

/** Trending tracks of a genre that anyone may stream: no paywall or app list, not explicit, song length. */
async function trending(genre: string): Promise<Track[]> {
  const q = new URLSearchParams({ genre, time: 'month', app_name: APP });
  const res = await fetch(`${API}/tracks/trending?${q}`, { signal: AbortSignal.timeout(15_000) });
  if (!res.ok) throw new Error('Audius ' + res.status);
  const data = ((await res.json()) as { data?: AudiusTrack[] }).data ?? [];
  return data
    .filter(
      (t) =>
        t.is_streamable !== false &&
        !t.is_stream_gated &&
        !t.is_unlisted &&
        !t.is_delete &&
        !t.user.is_deactivated &&
        !(t.allowed_api_keys && t.allowed_api_keys.length) &&
        t.parental_warning_type !== 'explicit' &&
        t.duration >= 90 &&
        t.duration <= 600,
    )
    .map((t) => ({
      id: t.id,
      title: t.title,
      artist: t.user.name,
      link: 'https://audius.co' + t.permalink,
      genre: t.genre,
      src: `${API}/tracks/${encodeURIComponent(t.id)}/stream?app_name=${APP}`,
    }));
}

export class Music {
  /** The playing element (read it, don't drive it from outside). */
  readonly audio = new Audio();
  private station: Station = 'mix';
  private queue: Track[] = [];
  private index = -1;
  private loading: Promise<void> | null = null;
  private wanted = false;
  private volume = 0.35;
  muted = false;
  private failures = 0;

  /** The levelling chain (made on the first play, which follows a click). */
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  private samples: Float32Array<ArrayBuffer> | null = null;
  /** Smoothed loudness of this track (mean square, -1 until measured), and the gain it calls for. */
  private energy = -1;
  private level = FALLBACK_GAIN;
  /** A new track started (for a "now playing" note). */
  onTrack: (t: Track) => void = () => {};
  /** The station's list couldn't be loaded (offline, Audius down). */
  onError: (msg: string) => void = () => {};

  constructor() {
    this.audio.preload = 'none';
    // Measuring the sound needs CORS (our files are same-origin; Audius streams send it).
    this.audio.crossOrigin = 'anonymous';
    this.audio.addEventListener('ended', () => this.next());
    // A track that won't play: skip it (but stop trying if nothing plays at all).
    this.audio.addEventListener('error', () => {
      if (!this.wanted || ++this.failures > 5) return;
      setTimeout(() => this.next(), 500);
    });
    this.audio.addEventListener('playing', () => (this.failures = 0));
  }

  get current(): Track | null {
    return this.queue[this.index] ?? null;
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    this.apply();
  }

  setMuted(m: boolean) {
    this.muted = m;
    this.apply();
  }

  setStation(s: Station) {
    if (s === this.station) return;
    this.station = s;
    this.queue = [];
    this.index = -1;
    this.audio.removeAttribute('src');
    this.audio.load();
    if (this.wanted) void this.setPlaying(true);
  }

  private get audible() {
    return !this.muted && this.volume > 0;
  }

  private setGain(seconds = 0.05) {
    const user = this.muted ? 0 : sliderGain(this.volume);
    if (!this.ctx || !this.gain) {
      this.audio.volume = user * FALLBACK_GAIN;
      return;
    }
    this.audio.volume = 1;
    this.gain.gain.setTargetAtTime(user * this.level, this.ctx.currentTime, seconds);
  }

  /** The levelling chain: element → gain → limiter → speakers, and a weighted copy to measure. */
  private levelling() {
    if (this.ctx) return;
    try {
      const ctx = new AudioContext();
      const src = ctx.createMediaElementSource(this.audio);
      const gain = ctx.createGain();
      // A safety limiter: a sudden loud part can't jump out before the leveller catches it.
      const limit = ctx.createDynamicsCompressor();
      limit.threshold.value = -6;
      limit.knee.value = 0;
      limit.ratio.value = 20;
      limit.attack.value = 0.003;
      limit.release.value = 0.25;
      src.connect(gain).connect(limit).connect(ctx.destination);
      const low = ctx.createBiquadFilter();
      low.type = 'highpass';
      low.frequency.value = 100;
      const presence = ctx.createBiquadFilter();
      presence.type = 'highshelf';
      presence.frequency.value = 1500;
      presence.gain.value = 4;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      // Silent output so the measuring branch keeps running.
      const mute = ctx.createGain();
      mute.gain.value = 0;
      src.connect(low).connect(presence).connect(analyser).connect(mute).connect(ctx.destination);
      this.ctx = ctx;
      this.gain = gain;
      this.analyser = analyser;
      this.samples = new Float32Array(analyser.fftSize);
      window.setInterval(() => this.measure(), LEVEL_MS);
      this.setGain(0);
    } catch {
      // No WebAudio: plain volume, not levelled.
    }
  }

  /** One loudness reading: ease the gain toward the level this track needs. */
  private measure() {
    const a = this.analyser;
    const buf = this.samples;
    if (!a || !buf || this.audio.paused) return;
    a.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const ms = sum / buf.length;
    if (ms < SILENCE) return;
    // Louder parts are caught within about a second (a drop after a quiet intro mustn't blast);
    // quieter parts lift slowly (a ~5 s average, so the music doesn't pump).
    const louder = ms > this.energy;
    this.energy = this.energy < 0 ? ms : this.energy + (ms - this.energy) * (louder ? 0.12 : 0.02);
    const db = 10 * Math.log10(this.energy);
    this.level = Math.min(MAX_BOOST, Math.max(MIN_GAIN, Math.pow(10, (TARGET_DB - db) / 20)));
    this.setGain(louder ? 0.15 : 1.5);
  }

  private apply() {
    this.setGain();
    // Nothing to hear: don't keep streaming.
    if (this.wanted && this.audible) void this.setPlaying(true);
    else this.audio.pause();
  }

  /** Play (true) on built-in maps, stop (false) elsewhere. Call again after a click to unlock audio. */
  async setPlaying(on: boolean) {
    this.wanted = on;
    if (!on || !this.audible) {
      this.audio.pause();
      return;
    }
    this.levelling();
    if (this.ctx?.state === 'suspended') void this.ctx.resume().catch(() => {});
    if (!this.queue.length) {
      await this.fill();
      if (!this.queue.length || !this.wanted) return;
      this.index = 0;
      this.load();
    }
    await this.resume();
  }

  /** Skip to the next track. */
  next() {
    if (!this.queue.length) return;
    this.index++;
    if (this.index >= this.queue.length) {
      // Played them all: a fresh shuffle (and a fresh trending list).
      const last = this.current;
      this.queue = [];
      void this.fill().then(() => {
        if (!this.queue.length) return;
        if (this.queue[0].id === last?.id && this.queue.length > 1) this.queue.push(this.queue.shift()!);
        this.index = 0;
        this.load();
        if (this.wanted && this.audible) void this.resume();
      });
      return;
    }
    this.load();
    if (this.wanted && this.audible) void this.resume();
  }

  private fill(): Promise<void> {
    this.loading ??= (async () => {
      try {
        const st = this.station;
        const genres = st === 'mix' ? Object.values(GENRES) : st === 'lasboi' ? [] : [GENRES[st]];
        const lists = await Promise.all(genres.map((g) => trending(g).catch(() => [] as Track[])));
        let all = shuffle(lists.flat());
        if (st === 'lasboi') all = shuffle(LASBOI_TRACKS);
        // The mix plays one of our own songs every third track (they'd be few among Audius' lists).
        else if (st === 'mix') {
          const own = shuffle(LASBOI_TRACKS);
          const mixed: Track[] = [];
          for (let i = 0; own.length || i < all.length; i++) {
            if (own.length && (mixed.length % 3 === 0 || i >= all.length)) mixed.push(own.pop()!);
            if (i < all.length) mixed.push(all[i]);
          }
          all = mixed;
        }
        if (!all.length) this.onError("Couldn't load music from Audius right now.");
        this.queue = all;
      } finally {
        this.loading = null;
      }
    })();
    return this.loading;
  }

  private load() {
    const t = this.queue[this.index];
    this.audio.src = t.src;
    // A new track starts no louder than unity until it's measured (the last one may have been boosted).
    this.energy = -1;
    this.level = Math.min(this.level, 1);
    this.setGain(0);
    this.onTrack(t);
  }

  private async resume() {
    if (!this.audio.src || !this.audio.paused) return;
    try {
      await this.audio.play();
    } catch {
      // Not allowed yet (no click since the page opened): the next setPlaying() tries again.
    }
  }
}

function shuffle<T>(a: readonly T[]): T[] {
  const out = [...a];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
